import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { EntitiesTableList, useDisableUserModal } from '../../../components';
import { fetchUsers, clearUsersInfo, changeUserStatus, deleteUserRole } from '../../../store/users';
import { resendInvitationForUser, sendNewPassword } from '../../../store/authorization';
import { useCurrentRole } from '../../../hooks/useCurrentRole';
import { useSearch } from '../../../hooks/useSearch';
import { renderColumns, transformData } from './renderColumns';
import { usePagination } from '../../../hooks/usePagination';
import { redirectToPage, renderRecordValue } from '../../../utils/helpers';
import { Navigation } from '../../../utils/navigation';

const Users = ({
  title,
  addNewBtnTitle,
  managedRole,
  users,
  myEmail,
  limit,
  currentPage,
  total,
  totalPages,
  fetchUsers,
  sendNewPassword,
  changeUserStatus,
  deleteUserRole,
  role,
  clearUsersInfo,
  resendInvitationForUser,
}) => {

  const navigate = Navigation();
  usePagination(navigate);

  const { searchValue, highlighted, handleChangeSearch } = useSearch(fetchUsers);
  const { isAdmin, isHOAdmin, isTCAdmin, isMHAdmin } = useCurrentRole(role);
  const [deleteUser, setDeleteUser] = useState(null);

  const handleConfirmDeleteUser = () => {
    deleteUserRole(deleteUser);
    setDisableUserModalVisible(false);
  };
  
  const { DisableUserModal, setDisableUserModalVisible } = useDisableUserModal({
    handleConfirm: handleConfirmDeleteUser,
    userName: renderRecordValue(deleteUser?.name),
    isDelete: true,
  });

  useEffect(() => {
    fetchUsers({ Navigation: navigate });

    return;
  }, [fetchUsers]);

  useEffect(() => () => clearUsersInfo(), [clearUsersInfo]);

  const handleManageUser = useCallback(
    (id, Navigation) => () => navigate.push(`${Navigation.location.pathname}/${id}/edit`),
    [navigate],
  );

  const handleSendPassword = useCallback((id) => () => sendNewPassword(id), [sendNewPassword]);

  const handleResendInvitation = useCallback((id) => () => resendInvitationForUser(id), [resendInvitationForUser]);

  const switchUserStatus = useCallback((payload) => () => changeUserStatus(payload), [changeUserStatus]);

  const archieveUserRole = useCallback((payload) => () => {
    setDeleteUser(payload);
    setDisableUserModalVisible(true)
  }, []);

  const handleChangePage = (page) => {
    redirectToPage(navigate?.location.pathname, page, navigate);
    fetchUsers({ value: searchValue, filtered: null, Navigation: navigate });
  };

  const tableData = useMemo(() => {
    const data = transformData({
      data: users,
      switchUserStatus,
      archieveUserRole,
      handleManageUser,
      myEmail,
      managedRole,
      isAdmin,
      isMHAdmin,
      isHOAdmin: false,
      handleSendPassword,
      handleResendInvitation,
      Navigation: navigate
    });


    return data;
  }, [users, handleManageUser, isAdmin, myEmail, handleSendPassword, managedRole, switchUserStatus, archieveUserRole, handleResendInvitation, navigate]);

  const columns = useMemo(() => {
    return role && users?.length > 0 ? renderColumns(isAdmin, isMHAdmin, isHOAdmin, searchValue, highlighted) : null;
  }, [role, users, isAdmin, highlighted, searchValue]);

  return (
    <>
    <EntitiesTableList
      title={title}
      addNewBtnTitle={addNewBtnTitle}
      Navigation={navigate}
      columns={columns}
      data={tableData}
      handleChangePage={handleChangePage}
      showAddNewButton={isAdmin}
      showAddNewAdminButton={isHOAdmin || isTCAdmin}
      handleChangeSearch={handleChangeSearch}
      searchValue={searchValue}
      limit={limit}
      currentPage={currentPage}
      total={total}
      totalPages={totalPages}
    />

    {DisableUserModal}
    </>
  );
};

const mapStateToProps = (state) => ({
  users: state.users.users,
  myEmail: state.authorization?.user?.email,
  limit: state.users.limit,
  currentPage: state.users.currentPage,
  total: state.users.total,
  totalPages: state.users.totalPages,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ fetchUsers, sendNewPassword, resendInvitationForUser, clearUsersInfo, changeUserStatus, deleteUserRole }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Users);
